import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const CompanyWorkshop = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Corporate Retreat - Mindful Leadership",
    "description": "Transform your team through mindful leadership and authentic connection. Corporate retreats for teams of 8-30 people.",
    "location": {
      "@type": "Place",
      "name": "Private Forest Venue",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "2800",
      "highPrice": "7500",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Corporate Retreat - Mindful Team Building in Nature"
        description="Transform your team through mindful leadership workshops in nature. 1-3 day corporate retreats for teams of 8-30 people."
        canonicalUrl="https://mysticforest.club/experiences/company-workshop"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Go Back Button */}
        <div className="pt-20 pb-4">
          <div className="max-w-6xl mx-auto px-6">
            <Button 
              variant="ghost" 
              onClick={handleGoBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Experiences
            </Button>
          </div>
        </div>
        
        {/* Hero Section */}
        <section className="relative py-32">
          <div className="absolute inset-0">
            <img 
              src="/lovable-uploads/a877abea-4a75-4e69-9428-39479f2cba09.png" 
              alt="Corporate team engaged in mindful leadership workshop in forest setting"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Corporate Retreat
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Transform your team through mindful leadership and authentic connection
            </p>
            <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
              Book Your Team Experience
            </Button>
          </div>
        </section>

        {/* Quick Info */}
        <section className="py-16 bg-card">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div className="flex flex-col items-center">
                <Calendar className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">When</h3>
                <p className="text-muted-foreground">Flexible scheduling</p>
                <p className="text-sm text-muted-foreground">Year-round availability</p>
              </div>
              <div className="flex flex-col items-center">
                <MapPin className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">Where</h3>
                <p className="text-muted-foreground">Private forest venue</p>
              </div>
              <div className="flex flex-col items-center">
                <Users className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">For Whom</h3>
                <p className="text-muted-foreground">Teams of 8-30 people</p>
              </div>
              <div className="flex flex-col items-center">
                <Clock className="w-8 h-8 mb-4 text-accent" />
                <h3 className="font-semibold mb-2">Duration</h3>
                <p className="text-muted-foreground">1-3 Days</p>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-light mb-8 text-center">
              Beyond Traditional Team Building
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-6">
                Move beyond surface-level activities to create lasting transformation in your organization. Our corporate retreats combine proven leadership development with mindfulness practices, creating an environment where authentic connections flourish and innovation thrives.
              </p>
              <p className="mb-6">
                Set in the tranquil beauty of our forest sanctuary, your team will step away from digital distractions and discover new ways of communicating, collaborating, and leading with presence and purpose.
              </p>
            </div>
          </div>
        </section>

        {/* Program Elements */}
        <section className="py-16 bg-card">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-serif font-light mb-12 text-center">
              Program Elements
            </h2>
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Mindful Leadership</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Develop emotional intelligence, authentic communication, and the ability to lead with presence rather than pressure.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Trust Building Exercises</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Nature-based activities that create psychological safety and deepen trust between team members.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Creative Problem Solving</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Breakthrough traditional thinking patterns using mindfulness and collaborative workshops in inspiring natural settings.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Sustainable Practices Integration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Learn to integrate mindfulness and well-being practices into daily work life for sustained high performance.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Investment */}
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6">
                <CardHeader>
                  <CardTitle>1-Day Intensive</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold mb-4">$2,800</div>
                  <p className="text-muted-foreground text-sm mb-4">For teams up to 15 people</p>
                  <ul className="text-sm text-muted-foreground text-left space-y-1">
                    <li>• 6-hour program</li>
                    <li>• Lunch & refreshments</li>
                    <li>• Materials included</li>
                    <li>• Follow-up resources</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="p-6">
                <CardHeader>
                  <CardTitle>3-Day Retreat</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold mb-4">$7,500</div>
                  <p className="text-muted-foreground text-sm mb-4">For teams up to 15 people</p>
                  <ul className="text-sm text-muted-foreground text-left space-y-1">
                    <li>• 2 nights accommodation</li>
                    <li>• All meals included</li>
                    <li>• Full program facilitation</li>
                    <li>• 3-month follow-up support</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            <Button size="lg" className="w-full mt-8" onClick={handleBooking}>
              Schedule Consultation
            </Button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CompanyWorkshop;
